import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { Technician, ViewMode } from '@shared/types';
import type { Tool } from '@shared/types/inventory';
import { Header } from '@shared/components/Layout/Header';
import { MonthView } from '@features/planning/components/Calendar/MonthView';
import { YearView } from '@features/planning/components/Calendar/YearView';
import { useAuth } from '@features/auth/context/AuthContext';
import { TechnicianAssignmentsPage } from '@features/planning/pages/TechnicianAssignmentsPage';
import { TechnicianAssignmentView } from '@features/planning/components/Assignment/TechnicianAssignmentView';
import { TechnicianInventoryView } from '@features/inventory/components/TechnicianInventoryView';
import { TechnicianVehicleView } from '@features/fleet/components/TechnicianVehicleView';
import { LogOut, CalendarDays, Briefcase, X, Bell, Wrench, Truck } from 'lucide-react';
import { useAssignmentNotifications } from '@shared/hooks/useAssignmentNotifications';
import { NotificationToast } from '@shared/components/NotificationToast';

interface TechnicianViewProps {
    allTechnicians: Technician[];
    tools: Tool[];
    onUpdateTools: (tools: Tool[]) => void;
    onUpdateTechnician: (techId: string, updates: Partial<Technician>) => void;
    onUpdateRemarque: (assignmentId: string, remarque: string) => void;
    onStartWorksite: (assignmentId: string) => void;
    onFinishWorksite: (assignmentId: string) => void;
}

export const TechnicianView: React.FC<TechnicianViewProps> = ({
    allTechnicians,
    tools,
    onUpdateTools,
    onUpdateTechnician,
    onUpdateRemarque,
    onStartWorksite,
    onFinishWorksite,
}) => {
    const { user, logout } = useAuth();
    const [currentDate, setCurrentDate] = useState(new Date(2026, 0, 1));
    const [viewMode, setViewMode] = useState<ViewMode>('month');
    const [showWeekends, setShowWeekends] = useState(true);

    // Mobile Detection
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const [activeTab, setActiveTab] = useState<'planning' | 'assignments' | 'inventory' | 'vehicle'>(
        isMobile ? 'planning' : 'planning'
    );
    const [selectedAssignmentId, setSelectedAssignmentId] = useState<string | null>(null);

    // Find the technician associated with this user
    const myTechnician = allTechnicians.find(t => t.id === user?.technicianId);

    // Notification system
    const {
        newAssignments,
        dismissNotification,
        requestNotificationPermission,
        hasNotifications
    } = useAssignmentNotifications(user?.technicianId, true);

    // Request notification permission on mount
    useEffect(() => {
        requestNotificationPermission();
    }, []);

    if (!myTechnician) {
        return (
            <div className="min-h-screen flex items-center justify-center p-4">
                <div className="glass-panel p-8 text-center">
                    <p className="text-red-400">Erreur: Profil technicien non trouvé</p>
                    <button onClick={logout} className="mt-4 glass-button text-gray-400">
                        Déconnexion
                    </button>
                </div>
            </div>
        );
    }

    // Services filter not needed for single technician
    const services = [myTechnician.service];

    // Find selected assignment
    const selectedAssignment = selectedAssignmentId
        ? myTechnician.assignments.find(a => a.id === selectedAssignmentId) || null
        : null;

    // Handle clicking assignment in calendar - show read-only view
    const handleAssignmentClick = (assignmentId: string) => {
        setSelectedAssignmentId(assignmentId);
    };

    // Mobile Bottom Nav Component
    const BottomNav = () => (
        <div className="fixed bottom-0 left-0 right-0 bg-[#0f172a]/90 backdrop-blur-md border-t border-white/10 z-50 pb-safe">
            <div className="flex justify-around items-center p-3">
                <button
                    onClick={() => setActiveTab('planning')}
                    className={`flex flex-col items-center gap-1 transition-colors ${activeTab === 'planning' ? 'text-primary' : 'text-gray-400'}`}
                >
                    <CalendarDays size={24} />
                    <span className="text-[10px] font-medium">Planning</span>
                </button>
                <button
                    onClick={() => setActiveTab('inventory')}
                    className={`flex flex-col items-center gap-1 transition-colors ${activeTab === 'inventory' ? 'text-emerald-400' : 'text-gray-400'}`}
                >
                    <Wrench size={24} />
                    <span className="text-[10px] font-medium">Outillage</span>
                </button>
                <button
                    onClick={() => setActiveTab('vehicle')}
                    className={`flex flex-col items-center gap-1 transition-colors ${activeTab === 'vehicle' ? 'text-blue-400' : 'text-gray-400'}`}
                >
                    <Truck size={24} />
                    <span className="text-[10px] font-medium">Véhicule</span>
                </button>
                <button
                    onClick={logout}
                    className="flex flex-col items-center gap-1 transition-colors text-gray-400 hover:text-red-400"
                >
                    <LogOut size={24} />
                    <span className="text-[10px] font-medium">Sortir</span>
                </button>
            </div>
        </div>
    );

    return (
        <div className={`min-h-screen ${isMobile ? 'pb-24 pt-4 px-4' : 'p-4 md:p-8'} max-w-7xl mx-auto`}>
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <div
                        className="w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center font-bold text-white shadow-lg text-sm md:text-base"
                        style={{ backgroundColor: myTechnician.color }}
                    >
                        {myTechnician.name.charAt(0)}
                    </div>
                    <div>
                        <h1 className="text-lg md:text-xl font-bold text-gray-200">
                            {isMobile ? 'Mon Planning' : 'Mon Espace'}
                        </h1>
                        <p className="text-xs md:text-sm text-gray-400">{myTechnician.name} - {myTechnician.service}</p>
                    </div>
                </div>

                {!isMobile && (
                    <button
                        onClick={logout}
                        className="glass-button text-gray-400 hover:text-red-400 hover:border-red-400/50 flex items-center gap-2"
                    >
                        <LogOut size={18} />
                        <span className="hidden md:inline">Déconnexion</span>
                    </button>
                )}
            </div>

            {/* Desktop Tab Navigation */}
            {!isMobile && (
                <div className="flex gap-2 mb-6 overflow-x-auto no-scrollbar pb-2">
                    <button
                        onClick={() => setActiveTab('planning')}
                        className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${activeTab === 'planning'
                            ? 'bg-primary/20 text-primary border border-primary/50'
                            : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'
                            }`}
                    >
                        <CalendarDays size={20} />
                        Planning
                    </button>
                    <button
                        onClick={() => setActiveTab('assignments')}
                        className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${activeTab === 'assignments'
                            ? 'bg-accent/20 text-accent border border-accent/50'
                            : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'
                            }`}
                    >
                        <Briefcase size={20} />
                        Chantiers
                    </button>
                    <button
                        onClick={() => setActiveTab('inventory')}
                        className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${activeTab === 'inventory'
                            ? 'bg-emerald-500/20 text-emerald-500 border border-emerald-500/50'
                            : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'
                            }`}
                    >
                        <Wrench size={20} />
                        Outillage
                    </button>
                    <button
                        onClick={() => setActiveTab('vehicle')}
                        className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${activeTab === 'vehicle'
                            ? 'bg-blue-500/20 text-blue-500 border border-blue-500/50'
                            : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'
                            }`}
                    >
                        <Truck size={20} />
                        Véhicule
                    </button>
                </div>
            )}

            {/* Content Area */}
            <AnimatePresence mode="wait">
                <motion.div
                    key={activeTab}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                >
                    {activeTab === 'planning' && (
                        <>
                            {isMobile ? (
                                // Mobile: Show Vertical Agenda directly for Planning
                                <TechnicianAssignmentsPage
                                    myTechnician={myTechnician}
                                    allTechnicians={allTechnicians}
                                    onUpdateRemarque={onUpdateRemarque}
                                    onStartWorksite={onStartWorksite}
                                    onFinishWorksite={onFinishWorksite}
                                />
                            ) : (
                                // Desktop: Show Calendar Grid
                                <>
                                    <Header
                                        currentDate={currentDate}
                                        setCurrentDate={setCurrentDate}
                                        viewMode={viewMode}
                                        setViewMode={setViewMode}
                                        services={services}
                                        selectedService={myTechnician.service}
                                        setSelectedService={() => { }}
                                        showWeekends={showWeekends}
                                        setShowWeekends={setShowWeekends}
                                    />
                                    <div className="transition-opacity duration-200">
                                        {viewMode === 'year' ? (
                                            <YearView
                                                currentDate={currentDate}
                                                technicians={[myTechnician]}
                                                showWeekends={showWeekends}
                                            />
                                        ) : (
                                            <MonthView
                                                currentDate={currentDate}
                                                technicians={[myTechnician]}
                                                allTechnicians={allTechnicians}
                                                onUpdateTechnician={onUpdateTechnician}
                                                showWeekends={showWeekends}
                                                onAssignmentClick={handleAssignmentClick}
                                                readOnly={true}
                                                viewMode={viewMode}
                                            />
                                        )}
                                    </div>
                                    <div className="mt-6 glass-panel p-4">
                                        <h3 className="text-sm font-semibold text-gray-300 mb-3">Légende</h3>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                                            <div className="flex items-center gap-2">
                                                <div className="w-4 h-4 rounded" style={{ backgroundColor: myTechnician.color, opacity: 0.3 }} />
                                                <span className="text-gray-400">Travail</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <div className="w-4 h-4 rounded bg-yellow-400/20" />
                                                <span className="text-gray-400">Congés</span>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </>
                    )}

                    {activeTab === 'assignments' && !isMobile && (
                        // Desktop only distinct tab, merged into Planning on mobile
                        <TechnicianAssignmentsPage
                            myTechnician={myTechnician}
                            allTechnicians={allTechnicians}
                            onUpdateRemarque={onUpdateRemarque}
                            onStartWorksite={onStartWorksite}
                            onFinishWorksite={onFinishWorksite}
                        />
                    )}

                    {activeTab === 'inventory' && (
                        <TechnicianInventoryView
                            myTechnician={myTechnician}
                            tools={tools}
                            onUpdateTools={onUpdateTools}
                        />
                    )}

                    {activeTab === 'vehicle' && myTechnician && (
                        <TechnicianVehicleView technicianId={myTechnician.id} />
                    )}
                </motion.div>
            </AnimatePresence>

            {/* Mobile Bottom Navigation */}
            {isMobile && <BottomNav />}

            {/* Assignment View Modal (for Calendar clicks - Desktop) */}
            {selectedAssignment && !isMobile && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-[#0f172a] border border-white/10 rounded-2xl w-full max-w-3xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col relative"
                    >
                        <button
                            onClick={() => setSelectedAssignmentId(null)}
                            className="absolute top-4 right-4 p-2 hover:bg-white/10 rounded-full text-gray-400 hover:text-white transition-colors z-10"
                        >
                            <X size={20} />
                        </button>
                        <div className="overflow-y-auto custom-scrollbar p-6">
                            <TechnicianAssignmentView
                                assignment={selectedAssignment}
                                technicians={allTechnicians}
                                onUpdateRemarque={onUpdateRemarque}
                                onStartWorksite={onStartWorksite}
                                onFinishWorksite={onFinishWorksite}
                            />
                        </div>
                    </motion.div>
                </div>
            )}

            {/* Notification Toast */}
            <NotificationToast
                assignments={newAssignments}
                onDismiss={dismissNotification}
            />

            {/* Notification Badge */}
            {hasNotifications && (
                <div className="fixed top-4 right-16 md:right-4 z-40">
                    <div className="relative">
                        <div className="bg-white/10 p-2 rounded-full backdrop-blur-md border border-white/10">
                            <Bell className="text-accent animate-pulse" size={20} />
                        </div>
                        <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-xs text-white font-bold shadow-lg shadow-red-500/50">
                            {newAssignments.length}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
};
